/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type {
  SearchExperimentId,
  SearchExperimentConfig,
  BuildSearchExperimentParamsOptions,
} from './types';
import { getUtagMainVId } from '@/utils/cookie';
import { getExperimentId } from './getExperimentId';

/**
 * Determines search experiment configuration based on experimentId and experimentEnabled flags.
 *
 * Expected Behavior Matrix:
 * | experimentId     | experimentEnabled (acesEnabled) | V2 Endpoint | experimentId Header | visitorId/userId Params |
 * |-----------------|--------------------------------|-------------|---------------------|-------------------------|
 * | 'vertex'        | true                           | ✅ Yes      | ✅ Sent             | ✅ Sent                 |
 * | 'elasticsearch' | true                           | ✅ Yes      | ✅ Sent             | ✅ Sent                 |
 * | undefined       | true                           | ✅ Yes      | ✅ Sent ('elasticsearch') | ✅ Sent         |
 * | any             | false                          | ❌ No (V1)  | ❌ NOT sent         | ❌ Not sent             |
 *
 * Note: When experimentEnabled is false, experimentId is ignored (non-ACES logic).
 * When experimentEnabled is true and experimentId is undefined, defaults to 'elasticsearch'.
 *
 * @param options - Configuration options
 * @param options.experimentId - The experiment ID from Monetate or other source
 * @param options.experimentEnabled - Whether search experiment V2 features are enabled (acesEnabled)
 * @returns Configuration object with flags for endpoint selection, headers, and params
 *
 */
export function getSearchExperimentConfig(options: {
  experimentId: SearchExperimentId | undefined;
  experimentEnabled: boolean;
}): SearchExperimentConfig {
  const { experimentId: rawExperimentId, experimentEnabled } = options;

  // Normalize experimentId using shared logic (handles ACES awareness and fallback)
  const experimentId = getExperimentId(rawExperimentId, experimentEnabled);

  // V2 endpoints only when both flags are true AND experimentId is vertex/elasticsearch
  const shouldUseV2Endpoint =
    experimentEnabled && (experimentId === 'vertex' || experimentId === 'elasticsearch');

  // Header sent for V2 endpoints (elasticsearch or vertex)
  const shouldSendExperimentIdHeader = shouldUseV2Endpoint;

  // Personalization params for both 'vertex' and 'elasticsearch'
  const shouldInjectVertexParams =
    experimentEnabled && (experimentId === 'vertex' || experimentId === 'elasticsearch');

  return {
    shouldUseV2Endpoint,
    shouldSendExperimentIdHeader,
    experimentIdHeader: shouldSendExperimentIdHeader ? experimentId : undefined,
    shouldInjectVertexParams,
  };
}

/**
 * Selects the appropriate URL based on search experiment configuration.
 *
 * @param config - Search experiment configuration from getSearchExperimentConfig
 * @param v1Url - V1 endpoint URL
 * @param v2Url - V2 endpoint URL
 * @returns The appropriate URL based on configuration
 */
export function selectSearchExperimentUrl(
  config: SearchExperimentConfig,
  v1Url: string,
  v2Url: string,
): string {
  return config.shouldUseV2Endpoint ? v2Url : v1Url;
}

/**
 * Builds search experiment headers object if experimentId header should be sent.
 *
 * @param config - Search experiment configuration from getSearchExperimentConfig
 * @returns Headers object with experimentId, or undefined if header should not be sent
 */
export function buildSearchExperimentHeaders(
  config: SearchExperimentConfig,
): { experimentId: 'vertex' | 'elasticsearch' } | undefined {
  if (!config.shouldSendExperimentIdHeader || !config.experimentIdHeader) {
    return undefined;
  }

  return {
    experimentId: config.experimentIdHeader,
  };
}

/**
 * Builds search experiment params with optional personalization parameters.
 *
 * @param config - Search experiment configuration from getSearchExperimentConfig
 * @param baseParams - Base parameters object to enhance
 * @param options - Optional visitorId and userId values
 * @returns Enhanced params object with personalization params if applicable (Vertex or Elasticsearch)
 */
export function buildSearchExperimentParams<T extends Record<string, unknown> | object>(
  config: SearchExperimentConfig,
  baseParams: T,
  options?: BuildSearchExperimentParamsOptions,
): T {
  if (!config.shouldInjectVertexParams) {
    return baseParams;
  }

  // Add personalization parameters (for Vertex AI and Elasticsearch)
  const visitorId = options?.visitorId || getUtagMainVId() || '';
  const userId = options?.userId || '';

  return {
    ...baseParams,
    visitorId,
    userId,
  };
}
